import { Component, computed, inject, OnInit } from '@angular/core';
import {
    NotificationsState,
    selectAllNotifications,
    selectNotificationCenter,
    SphereNotification
} from '../+state/notifications/notification.reducer';
import { createSelector, Store } from '@ngrx/store';
import { NotificationCenterActions, UINotificationActions } from '../+state/notifications/notification.actions';
import { NotificationCenterService } from './notification-center.service';
import { Observable } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import {
    NotificationUserSettings,
    sphereNotificationsUserSettingsGroup,
    SphereNotificationUserSettingKey
} from '../../settings/user-settings/+state/settings.models';
import { Router } from '@angular/router';
import { LetDirective } from '@ngrx/component';
import { NgClass } from '@angular/common';
import { MatDivider } from '@angular/material/divider';
import { NotificationCenterItemComponent } from './notification-center-item.component';
import { MatRipple } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { selectNotificationUserSettings, UserSettingsActions } from '../../settings/user-settings';

const selectFilteredNotifications = createSelector(
    selectAllNotifications,
    selectNotificationCenter,
    selectNotificationUserSettings,
    (notifications, notificationCenter, settings) => {
        let filtered = notifications;
        if (notificationCenter.errorsOnly) {
            filtered = filtered.filter(n => n.context === 'error');
        }
        if (settings[SphereNotificationUserSettingKey.showUnreadOnly]) {
            filtered = filtered.filter(n => !n.read);
        }
        return filtered;
    }
);

@Component({
    templateUrl: './notification-center.component.html',
    styleUrls: ['./notification-center.component.scss'],
    imports: [LetDirective, MatSlideToggle, NgClass, MatDivider, NotificationCenterItemComponent, MatRipple, MatIcon]
})
export class NotificationCenterComponent implements OnInit {
    private store = inject<Store<NotificationsState>>(Store);
    private notificationCenter = inject(NotificationCenterService);
    private router = inject(Router);

    public notificationCenter$ = this.store.select(selectNotificationCenter);
    public notifications = toSignal(this.store.select(selectFilteredNotifications), { initialValue: [] });
    public notificationSettings = toSignal(this.store.select(selectNotificationUserSettings), {
        initialValue: { showUnreadOnly: false } as NotificationUserSettings
    });

    ngOnInit() {
        this.store.dispatch(UINotificationActions.markAllNotificationsAsNotNew());
    }

    notificationClicked(notification: SphereNotification) {
        if (notification.url) {
            this.notificationCenter.closeNotificationCenter();
            this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                if (notification.url) {
                    this.router.navigate(notification.url.split('/'), { queryParamsHandling: 'merge', replaceUrl: true });
                }
            });
        } else {
            // TODO: Expand notification to display error details.
        }
        this.store.dispatch(UINotificationActions.updateNotification({ notification: { ...notification, read: true } }));
    }

    notificationReadStatusChange(notification: SphereNotification, readStatus: boolean) {
        this.store.dispatch(UINotificationActions.updateNotification({ notification: { ...notification, read: readStatus } }));
    }

    setNotificationFilter(mode: 'all' | 'errors') {
        this.store.dispatch(NotificationCenterActions.setErrorsOnly({ errorsOnly: mode === 'errors' }));
    }

    public showMarkAllAsRead = computed(() => this.notifications().length > 0);

    markAllAsRead() {
        this.store.dispatch(UINotificationActions.markAllNotificationsAsRead());
    }

    unreadOnlyChange(event: MatSlideToggleChange) {
        this.store.dispatch(
            UserSettingsActions.updateSetting({
                group: sphereNotificationsUserSettingsGroup,
                key: SphereNotificationUserSettingKey.showUnreadOnly,
                value: event.checked,
                optimistic: true
            })
        );
    }
}
